import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import GallerySection from '../components/GallerySection';
import TestimonialsSection from '../components/TestimonialsSection';
import { getBryllupspakker } from '../lib/cms';
import { imageUrl } from '../lib/images';

function extractPackageItems(htmlString) {
  if (!htmlString) return [];
  const liMatches = [...htmlString.matchAll(/<li[^>]*>(.*?)<\/li>/gi)];
  if (liMatches.length > 0) {
    return liMatches.map((m) => m[1].replace(/<[^>]+>/g, '').trim()).filter(Boolean);
  }
  const pMatches = [...htmlString.matchAll(/<p[^>]*>(.*?)<\/p>/gi)];
  if (pMatches.length > 0) {
    return pMatches.map((m) => m[1].replace(/<[^>]+>/g, '').trim()).filter(Boolean);
  }
  return htmlString
    .replace(/<[^>]+>/g, '\n')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function BryllupperPage() {
  const [pakker, setPakker] = useState([]);
  const [loadingPakker, setLoadingPakker] = useState(true);
  const [expandedPakker, setExpandedPakker] = useState({});

  const toggleExpand = (key) => {
    setExpandedPakker((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  useEffect(() => {
    async function loadPakker() {
      const data = await getBryllupspakker();
      setPakker(data);
      setLoadingPakker(false);
    }
    loadPakker();
  }, []);

  return (
    <>
      <SEO
        title="Bryllupper"
        description="Bryllupsfotograf og videograf med sans for ægte følelser, nærvær og uerstattelige øjeblikke. Se vores bryllupspakker og bryllupsgalleri her."
      />

      {/* Hero Section (Cinematic Dark Photography) */}
      <section className="relative min-h-[78vh] flex items-center justify-center pt-28 pb-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={imageUrl('/_assets/472331762-1068515591958554-2413136772439242105-n.jpg', 'width=1920,quality=85,format=auto')}
            alt="Brudepar på stranden ved Vesterhavet i gyldent aftenlys"
            className="w-full h-full object-cover filter brightness-[0.45] contrast-[1.05]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0C0C0D] via-[#0C0C0D]/40 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 text-center space-y-6">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-serif font-medium text-[#F4EFEA] tracking-tight leading-tight max-w-4xl mx-auto">
            De store følelser & de små uerstattelige øjeblikke
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-zinc-300 font-sans max-w-2xl mx-auto leading-relaxed font-normal">
            Jeres bryllup er fyldt med øjeblikke, der flyver afsted. Jeg fanger tårerne, latteren, berøringerne og festen i et tidløst, filmisk og naturligt udtryk.
          </p>

          <div className="pt-2 flex flex-wrap items-center justify-center gap-4">
            <a
              href="#pakker"
              className="cta-btn inline-flex items-center justify-center px-8 py-3.5 bg-white hover:bg-stone-100 text-[#0C0C0D] font-semibold text-sm tracking-wide transition-all shadow-lg"
            >
              Se bryllupspakker & priser
            </a>
            <Link
              to="/kontakt"
              className="cta-btn inline-flex items-center justify-center px-8 py-3.5 bg-transparent hover:bg-transparent text-white font-medium text-sm tracking-wide transition-all border border-white"
            >
              Tjek jeres dato
            </Link>
          </div>
        </div>
      </section>

      {/* Light Editorial Approach Section */}
      <section className="py-20 md:py-28 bg-white text-[#141416] border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
            <div className="md:col-span-5 aspect-[4/5] rounded-none overflow-hidden border border-zinc-200 shadow-lg">
              <img
                src={imageUrl('/_assets/469269727-1092454198947739-5094198924173448864-n.jpg', 'width=800,quality=85,format=auto')}
                alt="Brudepar hånd i hånd i naturen"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="md:col-span-7 space-y-6">
              <h2 className="text-3xl sm:text-4xl font-serif text-[#141416] leading-tight">
                Nærvær uden at forstyrre øjeblikket
              </h2>
              <div className="space-y-4 text-[#3A3836] text-sm md:text-base leading-relaxed font-sans">
                <p>
                  En bryllupsdag er fyldt med store følelser, spontan latter og de helt stille øjeblikke imellem. Min opgave som bryllupsfotograf er at være til stede uden at fylde, så I og jeres gæster kan være 100% i nuet.
                </p>
                <p>
                  Jeg kombinerer et dokumentarisk blik med sans for smukt, naturligt lys og æstetisk komposition. I skal ikke bekymre jer om at posere stift – vi går en afslappet tur sammen, griner og fanger kærligheden helt naturligt.
                </p>
              </div>
              <div className="pt-2">
                <a
                  href="#pakker"
                  className="cta-btn inline-flex items-center gap-2 px-7 py-3.5 bg-[#141416] hover:bg-black text-white text-sm font-medium transition-colors"
                >
                  Se bryllupspakker <i className="fa-solid fa-arrow-right text-xs" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dedicated Dynamic Gallery from CMS */}
      <GallerySection
        slug="bryllupper"
        title="Udvalgte Bryllupsbilleder"
        subtitle="Galleri & Stemning"
        className="bg-[#0C0C0D]"
      />

      {/* Bryllupspakker Section (Clean Light Gray Stationery Look) */}
      <section id="pakker" className="py-24 md:py-32 bg-[#F3F4F6] text-[#141416] border-y border-zinc-200 relative overflow-hidden">
        {/* Watermark in bottom right inside container alignment (lidt mindre end på forsiden) */}
        <div className="absolute right-4 sm:right-6 md:right-8 bottom-0 translate-y-6 sm:translate-y-4 md:translate-y-2 pointer-events-none select-none z-0">
          <img
            src={imageUrl('/_assets/unknown-1-1.png', 'width=800,quality=85,format=auto')}
            alt=""
            aria-hidden="true"
            className="w-36 sm:w-44 md:w-56 lg:w-64 xl:w-72 h-auto object-contain opacity-15 filter grayscale contrast-125"
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-medium text-[#141416] tracking-tight">
              Gennemskuelige pakker tilpasset jeres dag
            </h2>
            <p className="text-[#3A3836] text-base md:text-lg leading-relaxed max-w-2xl mx-auto font-medium">
              Alle bryllupper, ønsker og behov er forskellige. Så der sættes ingen fast pris før en samtale, hvor vi sammen gennemgår jeres dag og jeres ønsker.
            </p>
            <div className="pt-2">
              <a
                href="/_assets/bryllupsshoot-3.pdf"
                download="bryllupsshoot-lunde-media.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-stone-300 hover:border-black text-[#141416] text-xs sm:text-sm font-semibold tracking-wide transition-all shadow-sm hover:shadow"
              >
                <i className="fa-solid fa-file-pdf text-[#8C6B3D] text-sm" aria-hidden="true" />
                <span>Download bryllupspakker & priser (PDF)</span>
                <i className="fa-solid fa-arrow-down text-xs ml-0.5 text-stone-500" aria-hidden="true" />
              </a>
            </div>
          </div>

          {loadingPakker ? (
            <div className="py-12 text-center text-[#575450]">
              <i className="fa-solid fa-circle-notch animate-spin text-black mr-2" aria-hidden="true" />
              Henter bryllupspakker...
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
              {pakker.map((p) => {
                const pakkeKey = p.id || p.titel;
                const isExpanded = !!expandedPakker[pakkeKey];
                const items = extractPackageItems(p.detaljer);
                const visibleItems = isExpanded || items.length <= 4 ? items : items.slice(0, 4);
                const hasMore = items.length > 4;

                return (
                  <div
                    key={pakkeKey}
                    className="relative rounded-none flex flex-col justify-between transition-all duration-300 p-8 sm:p-10 bg-white border border-zinc-200 hover:border-zinc-400 shadow-sm hover:shadow-md"
                  >
                    <div className="space-y-6">
                      <div>
                        <div className="text-xs uppercase tracking-widest text-[#141416] font-semibold mb-1">
                          Bryllupspakke
                        </div>
                        <h3 className="text-2xl sm:text-3xl font-serif font-bold text-[#141416] tracking-wider">
                          {p.titel}
                        </h3>

                        {/* Priser fra bryllupsshoot */}
                        <div className="mt-4 pt-4 border-t border-zinc-200/80 space-y-2.5">
                          {p.pris_foto_video && (
                            <div className="flex items-baseline justify-between gap-2">
                              <span className="text-xs font-sans text-stone-600 font-medium">
                                Video & fotografering
                              </span>
                              <span className="text-lg sm:text-xl font-serif font-bold text-stone-900 tracking-tight">
                                {p.pris_foto_video}
                              </span>
                            </div>
                          )}
                          {p.pris_foto_eller_video && (
                            <div className="flex items-baseline justify-between gap-2">
                              <span className="text-xs font-sans text-stone-600 font-medium">
                                Video eller fotografering
                              </span>
                              <span className="text-base sm:text-lg font-serif font-semibold text-stone-800 tracking-tight">
                                {p.pris_foto_eller_video}
                              </span>
                            </div>
                          )}
                          {(!p.pris_foto_video && !p.pris_foto_eller_video) && (
                            <div className="text-sm text-[#141416] font-medium">
                              {p.pris || 'Pris aftales ved forudgående samtale'}
                            </div>
                          )}
                        </div>
                      </div>

                      {p.beskrivelse && (
                        <p className="text-zinc-600 text-sm leading-relaxed border-t border-zinc-200 pt-4">
                          {p.beskrivelse}
                        </p>
                      )}

                      {/* Inkluderet liste med udvidelse */}
                      <div className="space-y-3 pt-2">
                        <div className="text-xs uppercase tracking-wider font-semibold text-[#141416]">
                          <span>Indeholdt i {p.titel}:</span>
                        </div>

                        {items.length > 0 ? (
                          <div className="space-y-2.5">
                            <ul className="space-y-2.5">
                              {visibleItems.map((item, idx) => (
                                <li
                                  key={idx}
                                  className="flex items-start gap-2.5 text-[#2B2927] text-sm leading-relaxed"
                                >
                                  <span className="text-black font-bold shrink-0 select-none">✓</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>

                            {hasMore && (
                              <button
                                type="button"
                                onClick={() => toggleExpand(pakkeKey)}
                                className="mt-2 pt-2 border-t border-zinc-100 w-full inline-flex items-center justify-between text-xs font-semibold text-zinc-700 hover:text-black transition-colors cursor-pointer group py-1"
                                aria-expanded={isExpanded}
                              >
                                <span>
                                  {isExpanded
                                    ? 'Vis færre punkter'
                                    : 'Vis alle punkter'}
                                </span>
                                <i
                                  className={`fa-solid ${
                                    isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'
                                  } text-[11px] group-hover:translate-y-0.5 transition-transform`}
                                  aria-hidden="true"
                                />
                              </button>
                            )}
                          </div>
                        ) : (
                          <div
                            className="text-[#2B2927] text-sm leading-relaxed space-y-2.5 [&>ul]:space-y-2.5 [&>ul>li]:flex [&>ul>li]:items-start [&>ul>li]:gap-2.5 [&>ul>li]:before:content-['✓'] [&>ul>li]:before:text-black [&>ul>li]:before:font-bold [&>ul>li]:before:shrink-0"
                            dangerouslySetInnerHTML={{ __html: p.detaljer }}
                          />
                        )}
                      </div>
                    </div>

                    <div className="pt-8 mt-8 border-t border-[#EAE5DC]">
                      <Link
                        to={`/kontakt?pakke=${encodeURIComponent(p.titel)}`}
                        className="cta-btn w-full inline-flex items-center justify-center gap-2 py-3.5 px-6 font-medium text-sm tracking-wide transition-all bg-[#141416] hover:bg-black text-white shadow-sm"
                      >
                        <span>Forespørg på {p.titel}</span>
                        <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Kundens anbefaling */}
      <TestimonialsSection
        category="bryllup"
        title="Hvad brudeparret siger"
        subtitle="Læs en ægte anbefaling fra et af de brudepar, Michael har foreviget"
        theme="light"
      />

      {/* CTA Section (Cinematic Dark) */}
      <section className="py-20 bg-[#0C0C0D] border-t border-white/10 text-center">
        <div className="max-w-7xl mx-auto px-6 md:px-8 space-y-6">
          <h2 className="text-3xl sm:text-4xl font-serif text-[#F4EFEA] max-w-3xl mx-auto">
            Skal vi reservere jeres bryllupsdato?
          </h2>
          <p className="text-zinc-400 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto">
            Populære datoer i sommerhalvåret bookes ofte i god tid. Kontakt mig uforpligtende og hør, om jeres dato er ledig i kalenderen.
          </p>
          <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/kontakt?ydelse=bryllup"
              className="cta-btn inline-flex items-center justify-center px-8 py-4 bg-white hover:bg-stone-100 text-[#0C0C0D] font-semibold text-sm tracking-wide transition-all shadow-lg"
            >
              Book et uforpligtende kaffemøde
            </Link>
            <a
              href="tel:26368306"
              className="cta-btn inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent hover:bg-transparent text-white font-medium text-sm tracking-wide transition-all border border-white"
            >
              <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
              <span className="whitespace-nowrap">26 36 83 06</span>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
